import React, { useEffect, useRef, useState } from 'react'

import {
  Box,
  FormControlLabel,
  FormHelperText,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core'
import { Controller, useFormContext } from 'react-hook-form'

import { ConfirmationDialog, Label, SelectCountry } from 'components'

import useSelectCountry from 'hooks/useSelectCountry'
import useSnackbar from 'hooks/useSnackbar'
import useThirdPartyManagementStepper from 'hooks/useThirdPartyManagementStepper'

import * as service from 'service'
import helpers from 'helpers'
import theme from 'theme'
import useStyles from './styles'

const InternationalTransfers = () => {
  const { setIsLoading, companySupplier, isShow } =
    useThirdPartyManagementStepper()
  const { errors, control, watch, setValue, getValues } = useFormContext()
  const classes = useStyles()
  const { selectedCountries, setSelectedCountries } = useSelectCountry()
  const snackbar = useSnackbar()

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [countryPendingRemoval, setCountryPendingRemoval] = useState(null)
  const [dialogLoading, setDialogLoading] = useState(false)

  const didInitializeTransfers = useRef(false)

  const companySupplierDetail = companySupplier?.companySupplierDetail

  const hasInternationalTransfer = watch('hasInternationalTransfer')

  const handleRemoveCountry = (country) => {
    setCountryPendingRemoval(country)
    setOpenConfirmDialog(true)
  }

  const handleConfirmRemove = async () => {
    try {
      setDialogLoading(true)
      setIsLoading(true)

      const internationalTransferCreated =
        companySupplierDetail?.internationalTransfers?.find(
          (internationalTransfer) =>
            internationalTransfer.country === countryPendingRemoval.country,
        )

      if (
        companySupplierDetail?.id &&
        companySupplier?.id &&
        internationalTransferCreated
      ) {
        await service.dponet.suppliers.internationalTransfers.destroy({
          companySupplierId: companySupplier?.id,
          companySupplierDetailId: companySupplierDetail?.id,
          internationalTransferId: internationalTransferCreated.id,
        })
      }

      setSelectedCountries((prev) =>
        prev.filter((country) => country.id !== countryPendingRemoval.id),
      )

      snackbar.open({
        message: 'País de destino removido com sucesso',
        variant: 'success',
      })

      setOpenConfirmDialog(false)
      setCountryPendingRemoval(null)
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao remover país de destino',
        variant: 'error',
      })
      setOpenConfirmDialog(false)
      setCountryPendingRemoval(null)
    } finally {
      setDialogLoading(false)
      setIsLoading(false)
    }
  }

  const handleCloseConfirmDialog = (isOpen) => {
    if (!isOpen && !dialogLoading) {
      setCountryPendingRemoval(null)
    }
    setOpenConfirmDialog(isOpen)
  }

  useEffect(() => {
    if (!didInitializeTransfers.current) return

    setValue('internationalTransfers', selectedCountries, {
      shouldValidate: false,
    })
  }, [selectedCountries])

  useEffect(() => {
    const initialTransfers = getValues('internationalTransfers')
    setSelectedCountries(initialTransfers || [])
    didInitializeTransfers.current = true
    // eslint-disable-next-line
  }, [])

  return (
    <>
      <Typography variant="h5">Transferência Internacional</Typography>
      <FormControlLabel
        control={
          <Controller
            name="hasInternationalTransfer"
            control={control}
            defaultValue={false}
            as={<Switch checked={!!hasInternationalTransfer} color="primary" />}
          />
        }
        label={
          <Typography variant="body2" className={classes.switchLabel}>
            Ocorre transferência internacional de dados
          </Typography>
        }
        disabled={isShow}
      />
      {hasInternationalTransfer && (
        <Box
          display="flex"
          flexDirection="column"
          gridGap={theme.spacing(2)}
          p={2}
          pb={4}
        >
          <Label description="Descrição">
            <Controller
              as={
                <TextField
                  color="primary"
                  variant="outlined"
                  multiline
                  minRows={4}
                  error={!!errors.internationalTransferDescription}
                  helperText={errors?.internationalTransferDescription?.message}
                  fullWidth
                  disabled={isShow}
                />
              }
              control={control}
              name="internationalTransferDescription"
            />
          </Label>
          <Controller
            name="internationalTransfers"
            control={control}
            defaultValue={[]}
            as={<input type="hidden" />}
          />
          <SelectCountry
            withTable
            withAddButton
            confirmBeforeRemove
            label="Quais os países de destino?"
            handleRemoveCountry={handleRemoveCountry}
            fieldName="internationalTransfers"
            disabled={isShow}
          />
          {!!errors?.internationalTransfers && (
            <FormHelperText error>
              {errors.internationalTransfers.message}
            </FormHelperText>
          )}
        </Box>
      )}
      <ConfirmationDialog
        open={openConfirmDialog}
        setOpen={handleCloseConfirmDialog}
        text="Você tem certeza que deseja remover este país de destino?"
        textButton="Remover"
        textButtonCancel="Cancelar"
        variant="error"
        loading={dialogLoading}
        actionAcceptButton={handleConfirmRemove}
        disableBackdropClick
      />
    </>
  )
}

export default InternationalTransfers
